import Box from "@mui/material/Box";
import type { SxProps } from "@mui/material/styles";
import { alpha, type Theme, useTheme } from "@mui/material/styles";
import { forwardRef, ReactNode } from "react";

/**
 * The shape every navbar pill shares — size, not colour.
 *
 * Exported because the environment switcher is a `<button>` rather than a
 * `<div>` and so cannot use the component itself. Restating the values there
 * is what let the two drift: a button takes `line-height: normal` from the
 * browser's own stylesheet instead of the theme's 1.5, which made that one
 * pill several pixels shorter than every badge beside it. `lineHeight` is
 * therefore explicit here, so the height cannot depend on which element it is
 * painted on.
 */
export const NAVBAR_PILL_SX: SxProps<Theme> = {
	display: "flex",
	alignItems: "center",
	px: 1.25,
	py: 0.5,
	borderRadius: 1,
	fontFamily: "monospace",
	fontSize: "0.85rem",
	lineHeight: 1.5,
	whiteSpace: "nowrap",
};

interface NavbarBadgeProps {
	/** Value for the data-component attribute on the pill. */
	dataComponent: string;
	/** Signal colour; omitted badges get the neutral wash. */
	bgcolor?: string;
	children: ReactNode;
}

/**
 * The monospace pill used for every environment badge in the navbar.
 *
 * forwardRef because these badges are wrapped in MUI Tooltip, which needs a
 * ref on the element it anchors to.
 */
export const NavbarBadge = forwardRef<HTMLDivElement, NavbarBadgeProps>(
	({ dataComponent, bgcolor, children, ...tooltipProps }, ref) => {
		const theme = useTheme();
		return (
			<Box
				{...tooltipProps}
				ref={ref}
				sx={{
					...NAVBAR_PILL_SX,
					bgcolor: bgcolor ?? alpha(theme.palette.common.white, 0.15),
					color: alpha(theme.palette.common.white, 0.9),
					cursor: "default",
					gap: 0.5,
				}}
				data-component={dataComponent}
			>
				{children}
			</Box>
		);
	}
);

NavbarBadge.displayName = "NavbarBadge";
